import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

type MemberRow = {
  id: string
  name: string
  title: string | null
  industry: string | null
  event: {
    id: string
    name: string | null
    date: string | null
    organization_id: string
    organization_name: string
  }
}

type Props = {
  committee_type: string
  title: string
  members: MemberRow[]
  filters: { search: string }
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ResearchCommitteeMembersIndex({ committee_type, title, members, filters }: Props) {
  const [search, setSearch] = useState(filters.search)
  const basePath = `/admin/research/${committee_type}_committee_members`

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      basePath,
      Object.fromEntries(Object.entries({ search }).filter(([, v]) => v !== '')),
      { preserveState: true },
    )
  }

  return (
    <>
      <Head title={`Research - ${title}`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearch} className="max-w-sm flex-1">
            <Input
              placeholder="Search by name or title..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>
          <p className="text-sm text-muted-foreground">
            {members.length} {members.length === 1 ? 'member' : 'members'} · added from each
            event&rsquo;s {title} section
          </p>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Professional Network</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map(member => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">
                    <Link
                      href={`/admin/research/committee_members/${member.id}`}
                      className="hover:underline"
                    >
                      {member.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{member.title || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{member.industry || '—'}</TableCell>
                  <TableCell>
                    <Link
                      href={`/admin/research/events/${member.event.id}`}
                      className="hover:underline"
                    >
                      {member.event.name || 'Untitled'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <Link
                      href={`/admin/research/organizations/${member.event.organization_id}`}
                      className="hover:underline"
                    >
                      {member.event.organization_name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(member.event.date)}
                  </TableCell>
                </TableRow>
              ))}
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    No members found. Add them from an event&rsquo;s {title} section.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}

ResearchCommitteeMembersIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
